import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

/**
 * Editor-specific tooltip components that handle proper rendering within Slate's DOM hierarchy.
 *
 * The key difference from the base Tooltip is that EditorTooltipContent always renders in a portal
 * at the root level. This prevents tooltips from being clipped by Slate's scrollable containers
 * and stacking contexts.
 *
 * Note: These should only be used within the SMS editor. For tooltips elsewhere, use the regular
 * Tooltip components from @/components/ui/tooltip.
 */

const EditorTooltip = TooltipPrimitive.Root;
const EditorTooltipTrigger = TooltipPrimitive.Trigger;

const EditorTooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(
  (
    {
      className,
      sideOffset = 4,
      ...props
    }: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
    ref
  ) => (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  )
);
EditorTooltipContent.displayName = 'EditorTooltipContent';

export { EditorTooltip, EditorTooltipTrigger, EditorTooltipContent };
